import { memo } from "react";
import { Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { ReportErrorDialog } from "./ReportErrorDialog";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import logo from "@/assets/klarfinans_logo_ny.webp";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { openCookieSettings } = useCookieConsent();

  const footerLinks = {
    "Tjänster": [
      { label: "Bolån", href: "/sv-SE/bolan" },
      { label: "Privatlån", href: "/sv-SE/privatlan" },
      { label: "Lån utan UC", href: "/sv-SE/lana-utan-uc" },
      { label: "Företagslån", href: "/sv-SE/foretagslan" },
    ],
    "Support": [
      { label: "Vanliga frågor", href: "/sv-SE/faq" },
      { label: "Kontakta oss", href: "/sv-SE/kontakt" },
      { label: "Klarspråk", href: "/sv-SE/klarsprak" },
    ],
    "Juridiskt": [
      { label: "Integritetspolicy", href: "/privacy" },
      { label: "Användarvillkor", href: "/terms" },
      { label: "Cookies", href: "/cookies" },
      { label: "Affiliateinformation", href: "/affiliate-disclosure" },
    ],
  };

  return (
    <footer className="bg-muted/50 border-t border-border">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
          {/* Company Info */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 space-y-4">
            <Link to="/" className="flex items-center">
              <img 
                src={logo}
                srcSet={`${logo} 500w`}
                sizes="150px"
                alt="Klarfinans - Jämför lån och kreditkort" 
                className="h-12 w-auto transition-opacity hover:opacity-90"
                width="150"
                height="51"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-sm text-muted-foreground">
              Jämför smart. Välj tryggt. Spara pengar.
            </p>
            <div className="space-y-2">
              <a
                href="mailto:info@klarfinans.se"
                className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>info@klarfinans.se</span>
              </a>
              <div className="flex items-start space-x-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5" />
                <span>Ladugatan 57<br />254 48 Helsingborg</span>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="space-y-4">
              <h3 className="font-semibold text-foreground text-base">{category}</h3>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                {category === "Juridiskt" && (
                  <>
                    <li>
                      <button
                        onClick={openCookieSettings}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        Cookie-inställningar
                      </button>
                    </li>
                    <li>
                      <ReportErrorDialog />
                    </li>
                  </>
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex items-center justify-center">
            <p className="text-sm text-muted-foreground text-center">
              © {currentYear} Klarfinans. Alla rättigheter förbehållna.
            </p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 pt-8 border-t border-border">
          <div className="bg-muted/50 rounded-lg p-6 space-y-3">
            <p className="font-semibold text-sm text-foreground">Viktigt att veta</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Affiliateinformation:</strong> Klarfinans är en jämförelsetjänst som samarbetar med långivare och finansiella institutioner. 
              Vi kan erhålla ersättning när du ansöker om eller tecknar produkter via våra länkar. Detta påverkar inte vilka produkter 
              vi visar eller i vilken ordning, och det tillkommer inga extra kostnader för dig. Vår tjänst är helt kostnadsfri.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Kreditvarning:</strong> Att låna pengar kostar pengar. Tänk på att du alltid ska kunna betala tillbaka dina lån i tid. 
              En betalningsanmärkning kan få stora konsekvenser för din framtida ekonomi. Behöver du hjälp med din skuldsituation, 
              kontakta din kommuns budget- och skuldrådgivning. Klarfinans uppmuntrar alltid till ansvarsfullt lånande.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Representativt exempel:</strong> Effektiv ränta från 4,95% till 23%. Återbetalningstid 1-15 år. 
              Lånebelopp 5 000 - 800 000 kr. Konkret räkneexempel: Lån om 200 000 kr över 10 år med 7,5% effektiv ränta 
              ger en månadskostnad på 2 374 kr och totalt belopp att betala om 284 880 kr. Räntan är individuell och 
              baseras på din kreditvärdighet.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default memo(Footer);